import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { RootState } from "src/common/state/store";
import { fetchAgents, createAgent, updateAgent, deleteAgent, RagAgent } from "../api/ragApi";

type CreateAgentData = Parameters<typeof createAgent>[0];
type UpdateAgentData = Parameters<typeof updateAgent>[1];

type RagAgentsStatus = "idle" | "loading" | "loaded" | "error";

interface IRagState {
  agents: RagAgent[];
  status: RagAgentsStatus;
  error: string | null;
}

const initialState: IRagState = {
  agents: [],
  status: "idle",
  error: null,
};

// Cached fetch: dedupes so "knowledge base" pickers can dispatch it on every
// mount without firing duplicate requests. Skips if a fetch is in flight or the
// list is already fresh.
export const fetchAgentsThunk = createAsyncThunk<RagAgent[], void, { state: RootState }>(
  "rag/fetchAgents",
  async () => fetchAgents(),
  {
    condition: (_, { getState }) => {
      const status = getState().rag.status;
      return status !== "loading" && status !== "loaded";
    },
  },
);

// Forced fetch: always hits the server, ignoring the cache. Used after a
// mutation and by the management list, which always wants authoritative state.
export const refetchAgentsThunk = createAsyncThunk<RagAgent[], void, { state: RootState }>(
  "rag/refetchAgents",
  async () => fetchAgents(),
);

// Agent mutations all funnel through these thunks so the single canonical
// agents list (state.rag.agents) is re-synced from the server right after the
// change. Every consumer — the management list AND the "knowledge base" pickers
// — reads that one list, so a newly created/edited/deleted agent shows up
// everywhere instantly, with correct server-computed counts and is_shared flag.

export const createAgentThunk = createAsyncThunk<
  RagAgent,
  CreateAgentData,
  { state: RootState; rejectValue: string }
>("rag/createAgent", async (data, { dispatch, rejectWithValue }) => {
  try {
    const agent = await createAgent(data);
    await dispatch(refetchAgentsThunk());
    return agent;
  } catch (err: any) {
    // Forward the backend message (e.g. "Agent limit reached") so the form can
    // map it to a user-facing alert; .unwrap() rethrows this string.
    return rejectWithValue(err?.response?.data?.error?.message || "");
  }
});

export const updateAgentThunk = createAsyncThunk<
  RagAgent,
  { id: number; data: UpdateAgentData },
  { state: RootState; rejectValue: string }
>("rag/updateAgent", async ({ id, data }, { dispatch, rejectWithValue }) => {
  try {
    const agent = await updateAgent(id, data);
    await dispatch(refetchAgentsThunk());
    return agent;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.error?.message || "");
  }
});

export const deleteAgentThunk = createAsyncThunk<number, number, { state: RootState }>(
  "rag/deleteAgent",
  async (id, { dispatch }) => {
    await deleteAgent(id);
    await dispatch(refetchAgentsThunk());
    return id;
  },
);

export const ragSlice = createSlice({
  name: "rag",
  initialState,
  reducers: {
    invalidateAgents: (state) => {
      state.status = "idle";
    },
  },
  extraReducers: (builder) => {
    // Both the cached and forced fetch resolve into the same canonical list.
    builder
      .addMatcher(isAnyOf(fetchAgentsThunk.pending, refetchAgentsThunk.pending), (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addMatcher(
        isAnyOf(fetchAgentsThunk.fulfilled, refetchAgentsThunk.fulfilled),
        (state, { payload }) => {
          state.agents = payload;
          state.status = "loaded";
        },
      )
      .addMatcher(
        isAnyOf(fetchAgentsThunk.rejected, refetchAgentsThunk.rejected),
        (state, { error }) => {
          state.status = "error";
          state.error = error.message ?? "Failed to fetch RAG agents";
        },
      );
  },
});

export const ragReducer = ragSlice.reducer;
export const { invalidateAgents } = ragSlice.actions;

export const getRagAgents = (state: RootState) => state.rag.agents;
export const getRagAgentsStatus = (state: RootState) => state.rag.status;
